import ProductSlider from '../Products/ProductSlider';
import ProductDisclosure from '../Products/ProductFAQDisclosure';
import NewProductItem from '../Products/NewProductItem';
import { 
    StarIcon, 
    DecorationStar1, 
    DecorationStar2, 
    DecorationStar3, 
    DecorationStar4, 
    DecorationStar5, 
    DecorationStar6, 
    DecorationStar7, 
    DecorationStar8,
    ReviewStar, 
    EditReviewIcon,
    DeleteReviewIcon
} from '@/utils/svgIcons';
import Image from 'next/image';
import Link from 'next/link';
import SmallBoyImage from '../../../public/images/little-boy.png';
import SmallGirlImage from '../../../public/images/little-girl.png';
import StarRatings from 'react-star-ratings';
import Button from '@/components/Button';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useMemo, useCallback, memo } from 'react';
import useUserPreferences from '@/hooks/useUserPreferences';
import { useCurrency } from '../DesktopHeader';

interface Props {
  product: any;
  name: string;
  gender: string;
  productGender: string;
  reviews: any[];
  averageReviewStars: number;
  settings: any;
  handlePersonalizeClick: () => void;
  check_in_wishlist: boolean;
  WishlistDel: (id: number) => void;
  user: any;
  reviewStar: number;
  review: string;
  setReview: (val: string) => void;
  setReviewStar: (val: number) => void;
  wordCount: number;
  isEditing: any;
  setIsEditing: (val: any) => void;
  handleCreate: () => void;
  handleUpdate: () => void;
  mutation: any;
  UpdateMutation: any;
  DeleteMutation: any;
  related_products: any[];
  addToWishlist: any;
  editReview: any;
  deleteReview: any;
  isLoading: any;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

// Memoized star rating component
const StarRating = memo(({ averageReviewStars }: { averageReviewStars: number }) => {
  const starsToShow = useMemo(() => {
    return (averageReviewStars === 0 || averageReviewStars === undefined || Number.isNaN(averageReviewStars)) ? 4 : averageReviewStars;
  }, [averageReviewStars]);

  return (
    <div className="flex gap-1 mt-4">
      {[...Array(5)].map((_, i) => (
        <span key={i} className={`w-6 h-6 ${i < starsToShow ? 'text-yellow-500' : 'text-gray-500'}`}>
          <StarIcon />
        </span>
      ))}
    </div>
  );
});
StarRating.displayName = 'StarRating';

// Memoized review item component
const ReviewItem = memo(({ 
  review, 
  user, 
  editReview, 
  deleteReview 
}: { 
  review: any; 
  user: any; 
  editReview: (id: number, stars: number, review: string) => void;
  deleteReview: (id: number) => void;
}) => {
  const isUserReview = user?.id === review.user_id;
  
  return (
    <div className={`${isUserReview ? 'bg-gray-100' : ''} w-full flex flex-col items-start justify-between py-2`}>
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <ReviewStar key={i} filled={review.stars > i} />
        ))}
      </div>
      <h2 className="font-semibold text-primary mt-1">{review.name}</h2>
      <div className="review-text text-text-color mt-4">{review.review}</div>
      {isUserReview && (
        <div className="review-actions flex gap-4 items-center justify-center mt-4">
          <button
            className="bg-success p-2 rounded-lg"
            onClick={() => editReview(review.id, review.stars, review.review)}
            aria-label="Edit review"
          >
            <EditReviewIcon />
          </button>
          <button
            className="bg-red p-2 rounded-lg"
            onClick={() => deleteReview(review.id)}
            aria-label="Delete review"
          >
            <DeleteReviewIcon />
          </button>
        </div>
      )}
      <hr className="h-[1px] bg-[#FEF4C1] w-full mt-8" />
    </div>
  );
});
ReviewItem.displayName = 'ReviewItem';

// Memoized product header component
const ProductHeader = memo(({ 
  product, 
  name, 
  productGender, 
  DemoName, 
  locale 
}: { 
  product: any; 
  name: string; 
  productGender: string; 
  DemoName: string; 
  locale: string;
}) => {
  const displayName = useMemo(() => name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en), [name, locale, product?.replace_name]);
  
  return (
    <div className={`flex items-center gap-2 min-w-0 mt-6`}>
      <h2 className="text-4xl font-normal flex flex-wrap items-center gap-1 break-words">
        {(DemoName || '').split('{basmti}')[0]}
        <span className="text-success-500 font-bold">{displayName}</span>
        {(DemoName || '').split('{basmti}')[1]}
      </h2>
      <span className="w-10 h-8 shrink-0">
        <Image
          src={productGender === 'Female' ? SmallGirlImage : SmallBoyImage}
          className="w-full"
          alt={productGender === 'Female' ? "small-girl" : "small-boy"}
          loading="lazy"
        />
      </span>
    </div>
  );
});
ProductHeader.displayName = 'ProductHeader';

// Memoized price and wishlist component
const PriceAndWishlist = memo(({ 
  product, 
  displayPrice, 
  preferencesCurrency, 
  check_in_wishlist, 
  WishlistDel, 
  addToWishlist 
}: { 
  product: any; 
  displayPrice: any; 
  preferencesCurrency: any; 
  check_in_wishlist: boolean; 
  WishlistDel: (id: number) => void; 
  addToWishlist: (id: number) => void;
}) => {
  const handleWishlistClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (check_in_wishlist) {
      WishlistDel(product.id);
    } else {
      addToWishlist(product.id);
    }
  }, [check_in_wishlist, product.id, WishlistDel, addToWishlist]);

  return (
    <div className="flex items-center justify-between mt-6">
      <div className='flex items-center gap-4'>
        <h2 className="text-4xl font-bold">
          <span>{displayPrice.symbol}</span>
          {displayPrice.amount}
        </h2>
        {product.has_sale && (
          <h3 className="text-red line-through text-2xl font-normal">
            <span>{preferencesCurrency.symbol}</span>
            {product?.price?.amount}
          </h3>
        )}
      </div>
      <button
        onClick={handleWishlistClick}
        className="border border-primary rounded-lg mr-4 flex items-center justify-center p-3"
        aria-label={check_in_wishlist ? "Remove from wishlist" : "Add to wishlist"}
      >
        <svg width={check_in_wishlist ? "23" : "24"} height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {check_in_wishlist ? (
            <path
              d="M20.1357 24C19.7657 23.999 19.3996 23.9247 19.0586 23.7813C18.7175 23.638 18.4083 23.4285 18.1487 23.165L11.9987 17.051L5.84866 23.169C5.45369 23.5697 4.94715 23.8422 4.39508 23.9508C3.843 24.0594 3.27101 23.9992 2.75366 23.778C2.23118 23.5678 1.7842 23.205 1.47112 22.7369C1.15803 22.2688 0.993376 21.7171 0.998658 21.154V5C0.998658 3.67392 1.52544 2.40215 2.46312 1.46447C3.40081 0.526784 4.67258 0 5.99866 0L17.9987 0C18.6553 0 19.3054 0.129329 19.9121 0.380602C20.5187 0.631876 21.0699 1.00017 21.5342 1.46447C21.9985 1.92876 22.3668 2.47996 22.618 3.08658C22.8693 3.69321 22.9987 4.34339 22.9987 5V21.154C23.0043 21.7167 22.8402 22.268 22.5278 22.7361C22.2155 23.2041 21.7694 23.5672 21.2477 23.778C20.8955 23.9253 20.5174 24.0008 20.1357 24Z"
              fill="#00214D"
            />
          ) : (
            <path
              d="M20.1371 24C19.7672 23.999 19.4011 23.9247 19.0601 23.7813C18.719 23.638 18.4097 23.4285 18.1501 23.165L12.0001 17.051L5.85012 23.169C5.45515 23.5697 4.94861 23.8422 4.39654 23.9508C3.84447 24.0594 3.27247 23.9992 2.75512 23.778C2.23264 23.5678 1.78567 23.205 1.47258 22.7369C1.15949 22.2688 0.994841 21.7171 1.00012 21.154V5C1.00012 3.67392 1.52691 2.40215 2.46459 1.46447C3.40227 0.526784 4.67404 0 6.00012 0L18.0001 0C18.6567 0 19.3069 0.129329 19.9135 0.380602C20.5202 0.631876 21.0714 1.00017 21.5357 1.46447C22 1.92876 22.3683 2.47996 22.6195 3.08658C22.8708 3.69321 23.0001 4.34339 23.0001 5V21.154C23.0057 21.7167 22.8417 22.268 22.5293 22.7361C22.217 23.2041 21.7709 23.5672 21.2491 23.778C20.8969 23.9253 20.5189 24.0008 20.1371 24ZM6.00012 2C5.20447 2 4.44141 2.31607 3.8788 2.87868C3.31619 3.44129 3.00012 4.20435 3.00012 5V21.154C2.99976 21.3206 3.04879 21.4836 3.14102 21.6224C3.23325 21.7612 3.36455 21.8695 3.51831 21.9337C3.67208 21.9979 3.84143 22.0151 4.00496 21.9831C4.1685 21.9512 4.31888 21.8714 4.43712 21.754L11.3001 14.933C11.4875 14.7468 11.7409 14.6422 12.0051 14.6422C12.2693 14.6422 12.5228 14.7468 12.7101 14.933L19.5651 21.752C19.6834 21.8694 19.8338 21.9492 19.9973 21.9811C20.1608 22.0131 20.3302 21.9959 20.4839 21.9317C20.6377 21.8675 20.769 21.7592 20.8612 21.6204C20.9535 21.4816 21.0025 21.3186 21.0021 21.152V5C21.0021 4.20435 20.6861 3.44129 20.1234 2.87868C19.5608 2.31607 18.7978 2 18.0021 2H6.00012Z"
              fill="#00214D"
            />
          )}
        </svg>
      </button>
    </div>
  );
});
PriceAndWishlist.displayName = 'PriceAndWishlist';

// Memoized review form component
const ReviewForm = memo(({
  reviewStar,
  setReviewStar,
  review,
  setReview,
  wordCount,
  isEditing,
  setIsEditing,
  handleCreate,
  handleUpdate,
  mutation,
  UpdateMutation,
  DeleteMutation,
  t,
  locale
}: any) => {
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing) handleCreate();
    else handleUpdate();
  }, [isEditing, handleCreate, handleUpdate]);

  const handleCancelEdit = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setReview('');
    setReviewStar(0);
    setIsEditing(undefined);
  }, [setReview, setReviewStar, setIsEditing]);

  const isLoading = mutation.isLoading || UpdateMutation.isLoading || DeleteMutation.isLoading;

  return (
    <div className="add-review w-full mt-10" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <h1 className="text-xl font-semibold">{t('reviewFormHeading')}</h1>
      <p className="font-medium mt-6">{t('reviewFormSubHeading')}</p>
      <div className="ratings-star mt-2">
        <StarRatings
          rating={reviewStar}
          starRatedColor="#FBDD37"
          starHoverColor="#FBDD37"
          starDimension="21px"
          starSpacing="2"
          changeRating={setReviewStar}
          numberOfStars={5}
          name="rating"
        />
      </div>
      <form onSubmit={handleSubmit}>
        <textarea
          className="rounded-lg border border-gray-300 w-full mt-5 p-4 mb-2 text-sm placeholder-gray-400 font-light"
          rows={5}
          value={review}
          onChange={e => setReview(e.target.value)}
          placeholder={t('textAreaPlaceholder')}
        />
        <div className="flex items-center">
          <div className="text-gray-400 text-sm font-light">
            {280 - wordCount} {t('wordLimit')}
          </div>
          {isEditing && (
            <button
              onClick={handleCancelEdit}
              className="text-sm mr-2 text-gray-400"
            >
              ({t('editReviewButton')})
            </button>
          )}
        </div>
        <div className="btn mt-6">
          <Button
            type="submit"
            bg="primary"
            style="outline"
            isLoading={isLoading}
            classes={[
              'mt-6',
              'py-4',
              'bg-transparent',
              'rounded-md',
              'w-full',
              'border-[1px]',
              'border-primary-500',
              'text-primary-500',
              'text-base',
              'font-bold',
            ]}
          >
            {t('reviewSubmit')}
          </Button>
        </div>
      </form>
    </div>
  );
});
ReviewForm.displayName = 'ReviewForm';

const DesktopView = ({
  product,
  name,
  gender,
  productGender,
  reviews,
  averageReviewStars,
  settings,
  handlePersonalizeClick,
  check_in_wishlist,
  WishlistDel,
  user,
  reviewStar,
  review,
  setReview,
  setReviewStar,
  wordCount,
  isEditing,
  setIsEditing,
  handleCreate,
  handleUpdate,
  mutation,
  UpdateMutation,
  DeleteMutation,
  related_products,
  addToWishlist,
  editReview,
  deleteReview,
  isLoading,
  scrollRef
}: Props) => {
  const { t } = useTranslation('product');
  const { locale } = useRouter();
  const preferences = useUserPreferences();
  const preferencesCurrency = useCurrency(preferences?.currencyCode ?? "ILS");

  // Memoize derived values
  const displayPrice = useMemo(() => product.front_price, [product.front_price]);
  const lang = useMemo(() => locale === 'ar' ? 'ar' : 'en', [locale]);
  const FAQS = useMemo(() => {
    return Array.isArray(settings?.faqs?.[lang]) ? settings.faqs[lang] : [];
  }, [settings?.faqs, lang]);
  
  const DemoName = useMemo(() => {
    return locale === 'ar' 
      ? product?.demo_name?.ar || product?.demo_name?.en || '' 
      : product?.demo_name?.en || product?.demo_name?.ar || '';
  }, [locale, product?.demo_name]);

  const Description = useMemo(() => {
    const desc = locale === 'ar' ? product.description?.ar || '' : product.description?.en || '';
    const displayName = name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en);
    return desc.replace(new RegExp('{basmti}', 'g'), displayName);
  }, [locale, product.description, name, product?.replace_name]);

  const tags = useMemo(() => product.tags || [], [product.tags]);
  const hasMultipleImages = useMemo(() => product?.images?.length > 1, [product?.images?.length]);

  // Memoize review list
  const reviewList = useMemo(() => {
    if (!reviews?.length) return null;
    return reviews.map((item: any, index: number) => (
      <ReviewItem
        key={index}
        review={item}
        user={user}
        editReview={editReview}
        deleteReview={deleteReview}
      />
    ));
  }, [reviews, user, editReview, deleteReview]);

  // Memoize related products list
  const relatedProductsList = useMemo(() => {
    if (!related_products?.length) return null;
    return related_products.map((related: any, index: number) => (
      <NewProductItem 
        product={related} 
        key={`${related.id}-${index}`} 
        gender={gender} 
        name={name} 
      />
    ));
  }, [related_products, gender, name]);

  return (
    <div className="hidden lg:flex bg-white flex-col product-page mt-4 lg:relative z-10" ref={scrollRef}>
      <div className="min-w-4xl max-w-6xl mx-auto">
        {/* Product content layout */}
        <div className={`product-content container grid ${hasMultipleImages ? 'grid-cols-8' : 'grid-cols-2'}`}>
          <div className={`${hasMultipleImages ? 'col-span-5' : ''} ${locale === 'ar' ? 'order-1' : 'order-2'} overflow-hidden product-image mt-6 w-full h-[480px]`}>
            <ProductSlider images={product?.images || []} />
          </div>
          <div 
            className={`${hasMultipleImages ? 'col-span-3' : ''} ${locale === 'ar' ? 'order-2' : 'order-1'} product-description lg:flex lg:flex-col lg:justify-between mt-6 text-text-color ${locale === 'ar' ? 'mr-6' : 'ml-6'}`}
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div>
              {/* Discount and tag badges */}
              {(product.has_sale || tags.length > 0) && (
                <div className="flex items-center">
                  {product.has_sale && (
                    <div>
                      <span className={`product-discount desk hidden ${locale === 'ar' ? 'lg:inline-block' : 'hidden'} bg-red text-white py-2 px-6 rounded-lg`}>
                        {t('discount')} %{product.discount_percentage}
                      </span>
                      <span className={`product-discount desk hidden ${locale !== 'ar' ? 'lg:inline-block' : 'hidden'} bg-red text-white py-2 px-6 rounded-lg`}>
                        {product.discount_percentage}% {t('discount')}
                      </span>
                    </div>
                  )}
                  {tags.map((tag: any, index: number) => (
                    <div key={index} className="mr-2">
                      <span className="product-discount desk hidden lg:inline-block bg-red text-white py-2 px-5 rounded-lg">
                        {tag.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Product name and gender */}
              <ProductHeader 
                product={product}
                name={name}
                productGender={productGender}
                DemoName={DemoName}
                locale={locale === 'ar' ? 'ar' : 'en'}
              />
              
              {/* Product rating */}
              <StarRating averageReviewStars={averageReviewStars} />
              
              {/* Product description */}
              {Description && (
                <div
                  className="lg:mt-6 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: Description }}
                  dir={locale === 'ar' ? 'rtl' : 'ltr'}
                />
              )}
            </div>
            
            <div>
              {/* Product price and wishlist */}
              <PriceAndWishlist
                product={product}
                displayPrice={displayPrice}
                preferencesCurrency={preferencesCurrency}
                check_in_wishlist={check_in_wishlist}
                WishlistDel={WishlistDel}
                addToWishlist={addToWishlist}
              />
              
              {/* Personalize button */}
              <div className="product-actions flex mt-3">
                <button
                  className="text-white font-medium py-4 px-12 bg-primary block text-center rounded-lg w-full"
                  id="desktop-personalize-button"
                  onClick={handlePersonalizeClick}
                >
                  {t('personalizeButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Product FAQs */}
        {FAQS.length > 0 && (
          <div className="mt-20">
            {FAQS.map((faq: any, index: number) => (
              <ProductDisclosure key={`${faq.id || index}`} faq={faq} />
            ))}
          </div>
        )}
      </div>
      
      {/* Product reviews and form */}
      <div className="w-full max-w-5xl mx-auto lg:relative">
        <div className="w-full mt-10 py-12">
          {/* Reviews */}
          {reviews?.length > 0 && (
            <div className="flex flex-col w-full gap-4">
              {reviewList}
            </div>
          )}
          
          {/* Review form */}
          {user && (
            <ReviewForm
              reviewStar={reviewStar}
              setReviewStar={setReviewStar}
              review={review}
              setReview={setReview}
              wordCount={wordCount}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCreate={handleCreate}
              handleUpdate={handleUpdate}
              mutation={mutation}
              UpdateMutation={UpdateMutation}
              DeleteMutation={DeleteMutation}
              t={t}
              locale={locale}
            />
          )}
        </div>
        
        {/* Decorative stars - only show when not loading */}
        {!isLoading && (
          <span className="hidden lg:block absolute z-0">
            <DecorationStar1 />
            <DecorationStar2 />
            <DecorationStar3 />
            <DecorationStar4 />
            <DecorationStar5 />
            <DecorationStar6 />
            <DecorationStar7 />
            <DecorationStar8 />
          </span>
        )}
      </div>
      
      {/* Related products */}
      {related_products?.length > 0 && (
        <div className="products container-fluid mx-auto mt-20">
          <h2 className="text-primary font-semibold text-xl text-center">{t('relatedProducts')}</h2>
          <div className="products-list mt-6 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 lg:relative">
            {relatedProductsList}
          </div>
          <div className="browse-btn text-center mt-8">
            <Link href="/products" className="inline-block mt-6 border-2 border-primary text-primary text-center font-bold rounded-lg px-12 py-6">
              {t('viewMore')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DesktopView);